import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { driverVersionVersionOptions, driverVersions } from 'app/registries/consts/tabular-consts';
import { SSLMode, sslModeOptions } from '../consts/amazonredshift-consts';
const connectionForm: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'serverName',
            label: stringResources.ModelName_Host,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'port',
            label: stringResources.ModelName_Port,
            inputType: 'number',
            isLabelVertical: true,
            min: 0,
            defaultValue: 5439
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'databaseName',
            label: stringResources.ModelName_DatabaseName,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'userName',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'password',
        label: stringResources.ModelName_Password,
        enableAKVParameterization: true,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [{ type: 'required', errorMessage: '' }]
            }
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AmazonRedshift_DisplayText),
            stringResources.ModelName_Password
        )
    }
];

const sslMode: ControlMetadata[] = [
    {
        type: 'select-switch',
        bind: 'sslMode',
        label: stringResources.SSL_Mode,
        options: sslModeOptions,
        isLabelVertical: true,
        defaultValue: SSLMode.VerifyFull
    }
];

const sslInsecure: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'sslinsecure',
        label: stringResources.SSL_Insecure,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.True,
                value: true
            },
            {
                label: stringResources.False,
                value: false
            }
        ],
        defaultValue: false
    }
];

export const amazonRedshiftMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.amazonRedshift.linkedServiceType,
    displayName: stringResources.AmazonRedshift_DisplayText,
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: ResourceKind.AmazonRedshift
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-AmazonRedshift'
    },
    isFirstClass: true,
    fetchNestedNavTable: true,
    tableColumnNameQuotes: ['"', '"'],
    category: ['Database'],
    supportDatasetV2: true,
    supportFirstClassParameterExperience: true,
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    copySinkTypes: GlobalVariables.enableAmazonRedshiftSink ? [adfModelTypeMap.amazonRedshift.copySinkType] : [],
    showWarningMessage: '{ version !== "v2" }',
    warningMessage:  format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
            stringResources.VersionLabel.toLocaleLowerCase()),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-amazon-redshift',
    copySourceTypes: [adfModelTypeMap.amazonRedshift.copySourceType],
    supportCopyJobSource: true,
    supportCopyJobSink: GlobalVariables.enableAmazonRedshiftSink,
    supportOPDG: GlobalVariables.enableAmazonRedshiftSink ? GlobalVariables.enableOPDG : undefined,
    SHIRVersion: {
        v2: '5.54.0.0'
    },
    controls: 
        [        
            {
                type: 'radio-switch',
                bind: 'version',
                label: stringResources.VersionLabel,
                hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
                isLabelVertical: true,
                required: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                cases: [
                      {
                        caseValue: driverVersions.v1,
                        children: [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-amazon-redshift'
                            },
                            ...connectionForm
                        ]
                      },
                      {
                        caseValue: driverVersions.v2,
                        children: [
                            ...connectionForm,
                            ...sslMode,
                            ...sslInsecure
                        ]
                      }
                  ]
            },
            ...([...connectionForm, ...sslMode, ...sslInsecure]
                .map((control) => ({
                    ...control,
                    hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
                }))
            )
        ]
};
